import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../config/config.service';

const STORAGE_KEY = 'pos_invoice_number_pool';
const DEVICE_KEY = 'pos_device_id';

/** Poniżej tylu numerów pula jest uzupełniana przy najbliższej łączności. */
const PROG_UZUPELNIENIA = 5;

/** Ile numerów bierzemy naraz. Serwer i tak przycina do własnego limitu. */
const ROZMIAR_PULI = 20;

interface ZapisanaPula {
  companyId: string;
  numbers: string[];
}

/**
 * Pula numerów faktur na pracę bez łączności.
 *
 * Faktura wystawiona w trybie awaryjnym musi dostać OSTATECZNY numer w chwili
 * sprzedaży: numer trafia na wydruk nabywcy i do pliku FA(3), z którego liczy się kod
 * weryfikujący. Numer „tymczasowy", podmieniany po odzyskaniu łączności, unieważniłby
 * jedno i drugie.
 *
 * Numery pochodzą z serwera (`POST /ksef/invoice-numbers`), który rezerwuje je trwale —
 * KSeF odrzuca duplikat numeru u sprzedawcy (status 440), więc dwie kasy nadające ten
 * sam numer wystawiłyby dokument nie do przyjęcia, mając już wydruk u klienta.
 *
 * Pula jest przypisana do firmy: po przełączeniu firmy stara jest wyrzucana, bo numery
 * należą do jej ciągu numeracji.
 */
@Injectable({ providedIn: 'root' })
export class InvoiceNumberPoolService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _numbers = signal<string[]>([]);
  private readonly _companyId = signal<string | null>(null);

  readonly numbers = this._numbers.asReadonly();
  readonly count = computed(() => this._numbers().length);
  /** Czy da się wystawić fakturę bez łączności. */
  readonly canIssueOffline = computed(() => this._numbers().length > 0);
  readonly needsRefill = computed(() => this._numbers().length < PROG_UZUPELNIENIA);

  /**
   * Trwały identyfikator stanowiska. Serwer oddaje tej samej kasie jej wcześniejszą
   * pulę, więc restart nie dziurawi numeracji — pod warunkiem, że identyfikator
   * przeżywa restart.
   */
  deviceId(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return 'server';
    }
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
      id = c?.randomUUID ? c.randomUUID() : `pos-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  }

  /** Wczytuje pulę zapisaną na urządzeniu. Wołane po wybraniu firmy. */
  load(companyId: string): void {
    this._companyId.set(companyId);
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      this._numbers.set([]);
      return;
    }
    try {
      const zapisana = JSON.parse(raw) as ZapisanaPula;
      // Numery należą do ciągu numeracji jednej firmy — po przełączeniu firmy
      // stara pula jest bezużyteczna i myląca.
      this._numbers.set(zapisana.companyId === companyId ? (zapisana.numbers ?? []) : []);
    } catch {
      this._numbers.set([]);
    }
    this.persist();
  }

  /**
   * Uzupełnia pulę z serwera. Wymaga łączności — wołane po zalogowaniu i po jej
   * odzyskaniu. Błąd nie jest wyjątkiem: brak puli oznacza brak trybu awaryjnego,
   * a nie zablokowaną sprzedaż online.
   */
  async refill(pattern: NumberPatternRequest = {}): Promise<void> {
    const companyId = this._companyId();
    if (!companyId) {
      return;
    }

    const brakuje = ROZMIAR_PULI - this._numbers().length;
    if (brakuje <= 0) {
      return;
    }

    const resp = await firstValueFrom(
      this.http.post<{ numbers?: string[] }>(`${this.config.apiUrl()}/ksef/invoice-numbers`, {
        deviceId: this.deviceId(),
        count: ROZMIAR_PULI,
        prefix: pattern.prefix ?? '',
        year: pattern.year ?? '',
        padNumber: pattern.padNumber ?? false,
        minNumberLength: pattern.minNumberLength ?? 0,
      }),
    );

    // Serwer oddaje CAŁĄ pulę urządzenia (także numery wydane wcześniej), więc
    // podmieniamy ją w całości zamiast doklejać — inaczej numery by się dublowały.
    this._numbers.set(resp.numbers ?? []);
    this.persist();
  }

  /**
   * Zdejmuje numer z puli. Zwraca `null`, gdy pula jest pusta — wtedy kasa nie może
   * wystawić faktury bez łączności i musi to powiedzieć wprost.
   *
   * Zapis na urządzenie następuje natychmiast: numer zużyty przy sprzedaży nie może
   * wrócić do puli po nagłym zamknięciu aplikacji.
   */
  take(): string | null {
    const pula = this._numbers();
    if (pula.length === 0) {
      return null;
    }
    const [numer, ...reszta] = pula;
    this._numbers.set(reszta);
    this.persist();
    return numer;
  }

  /**
   * Oddaje numer do puli, gdy sprzedaż nie doszła do skutku PO jego pobraniu.
   * Numer wraca na początek, żeby ciąg pozostał rosnący.
   */
  giveBack(numer: string): void {
    if (!numer) {
      return;
    }
    this._numbers.set([numer, ...this._numbers().filter((n) => n !== numer)]);
    this.persist();
  }

  private persist(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const companyId = this._companyId();
    if (!companyId) {
      return;
    }
    const zapis: ZapisanaPula = { companyId, numbers: this._numbers() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(zapis));
  }
}

/** Wzorzec numeracji firmy — te same pola co przy pobraniu kolejnego numeru. */
export interface NumberPatternRequest {
  prefix?: string;
  year?: string;
  padNumber?: boolean;
  minNumberLength?: number;
}
